/**
 * "Who Is Still In The Center By Category" — ratio-view donut panel.
 * Each card shows ONLY "still here" counts (matches the section title).
 * Hover (desktop) or click/tap (mobile) on a card opens a compact dark popover
 * (rendered via portal so it always sits on top of sibling cards) listing the
 * actual names of children currently here, grouped per row.
 */
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { format } from "date-fns";
import { RefreshCw, Info, X, Clock } from "lucide-react";

const Donut = ({ value, total, color }) => {
  const radius = 32;
  const circumference = 2 * Math.PI * radius;
  const pct = total > 0 ? Math.min(value / total, 1) : 0;
  const offset = circumference * (1 - pct);
  return (
    <svg width="90" height="90" viewBox="0 0 90 90" data-testid="ratio-donut">
      <circle cx="45" cy="45" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="9" />
      <circle
        cx="45" cy="45" r={radius}
        fill="none" stroke={color} strokeWidth="9"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 45 45)"
        style={{ transition: 'stroke-dashoffset 0.5s ease' }}
      />
      <text x="45" y="44" textAnchor="middle" style={{ fontSize: '18px', fontWeight: 'bold', fill: color }}>
        {value}
      </text>
      <text x="45" y="58" textAnchor="middle" style={{ fontSize: '10px', fill: '#6b7280' }}>
        of {total}
      </text>
    </svg>
  );
};

const RatioCard = ({ title, color, bg, border, dot, stillIn, total, rows, testid }) => {
  const pct = total > 0 ? Math.round((stillIn / total) * 100) : 0;
  const [isOpen, setIsOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [popPos, setPopPos] = useState({ top: 0, left: 0, width: 220 });
  const cardRef = useRef(null);
  const popoverRef = useRef(null);

  const showPopover = isOpen || isHovering;
  const safeRows = rows || [];

  // Position popover OVER the card itself (overlay), so it's clearly attached
  // and never gets clipped by sections below.
  useEffect(() => {
    if (!showPopover || !cardRef.current) return;
    const update = () => {
      const r = cardRef.current.getBoundingClientRect();
      // Match the card's width (with a small inset) and sit at the card's top
      const width = Math.min(220, r.width - 8);
      const left = r.left + (r.width - width) / 2;
      const top = r.top + 6;
      setPopPos({ top, left, width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [showPopover]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        cardRef.current && !cardRef.current.contains(e.target) &&
        popoverRef.current && !popoverRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const popoverNode = showPopover ? (
    <div
      ref={popoverRef}
      className="fixed bg-gray-900 text-white p-2 rounded-md shadow-2xl ring-1 ring-gray-700"
      style={{
        zIndex: 100000,
        top: popPos.top,
        left: popPos.left,
        width: popPos.width || 220,
        maxHeight: 'calc(100vh - 40px)',
        animation: 'fadeIn 0.12s ease-out',
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      data-testid={`${testid}-popover`}
    >
      <button
        onClick={(e) => { e.stopPropagation(); setIsOpen(false); setIsHovering(false); }}
        className="absolute top-1 right-1 p-0.5 hover:bg-gray-700 rounded"
        aria-label="Close"
      >
        <X className="w-3 h-3" />
      </button>
      <p className="font-bold mb-1 text-[11px] uppercase tracking-wide border-b border-gray-700 pb-1 pr-4">
        {title} ({stillIn})
      </p>
      {stillIn === 0 ? (
        <p className="text-[11px] text-gray-300 italic">No children currently here</p>
      ) : (
        <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
          {safeRows.filter(r => (r.value || 0) > 0).map((r, i) => (
            <div key={i}>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{r.label} ({r.value})</p>
              <ul className="text-[11px] leading-tight space-y-0.5 mt-0.5 pl-1">
                {(r.names || []).map((name, idx) => (
                  <li key={idx} className="break-words">• {name}</li>
                ))}
                {(!r.names || r.names.length === 0) && (
                  <li className="text-gray-500 italic">No names available</li>
                )}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  ) : null;

  return (
    <>
      <div
        ref={cardRef}
        className="rounded-xl p-3 flex flex-col cursor-pointer hover:shadow-lg transition-shadow"
        style={{ background: bg, border: `2px solid ${border}`, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
        data-testid={testid}
        onClick={(e) => { e.preventDefault(); setIsOpen((v) => !v); }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onTouchEnd={(e) => { e.preventDefault(); setIsOpen((v) => !v); }}
        role="button"
        tabIndex={0}
        aria-label={`${title} still in center, click to see names`}
      >
        <h4 className="text-center text-sm font-bold uppercase tracking-wide" style={{ color }}>{title}</h4>
        <p className="text-center text-xs font-semibold mb-2" style={{ color }}>Still In: <span className="font-bold">{stillIn}</span></p>
        <div className="flex items-center justify-center gap-3 flex-1">
          <Donut value={stillIn} total={total} color={color} />
          <ul className="text-xs space-y-1.5 min-w-0">
            {safeRows.map((r, i) => (
              <li key={i} className="flex items-center gap-2 whitespace-nowrap">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dot }} />
                <span className="text-gray-700">
                  {r.label}: <span className="font-bold" style={{ color }}>{r.value}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <p className="text-center text-xs font-medium mt-2 pt-2 border-t" style={{ color, borderColor: border }}>
          {pct}% of signed in
        </p>
      </div>
      {popoverNode && createPortal(popoverNode, document.body)}
    </>
  );
};

// Small amber pill shown on Dashboard Home when any child is still signed in
// past their configured Still Here Reminder time — hover (desktop) / tap
// (mobile) reveals names, reusing the same portal-popover pattern as the
// ratio cards above so positioning/animation stays consistent.
const PastReminderBadge = ({ count, names }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [popPos, setPopPos] = useState({ top: 0, left: 0 });
  const badgeRef = useRef(null);
  const popoverRef = useRef(null);
  const showPopover = isOpen || isHovering;

  useEffect(() => {
    if (!showPopover || !badgeRef.current) return;
    const update = () => {
      const r = badgeRef.current.getBoundingClientRect();
      setPopPos({ top: r.bottom + 6, left: Math.max(8, r.right - 220) });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [showPopover]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        badgeRef.current && !badgeRef.current.contains(e.target) &&
        popoverRef.current && !popoverRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  if (!count) return null;

  const popoverNode = showPopover ? (
    <div
      ref={popoverRef}
      className="fixed bg-gray-900 text-white p-2 rounded-md shadow-2xl ring-1 ring-gray-700"
      style={{
        zIndex: 100000,
        top: popPos.top,
        left: popPos.left,
        width: 220,
        maxHeight: 'calc(100vh - 40px)',
        animation: 'fadeIn 0.12s ease-out',
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      data-testid="past-reminder-badge-popover"
    >
      <button
        onClick={(e) => { e.stopPropagation(); setIsOpen(false); setIsHovering(false); }}
        className="absolute top-1 right-1 p-0.5 hover:bg-gray-700 rounded"
        aria-label="Close"
      >
        <X className="w-3 h-3" />
      </button>
      <p className="font-bold mb-1 text-[11px] uppercase tracking-wide border-b border-gray-700 pb-1 pr-4">
        Still Here Past Reminder Time ({count})
      </p>
      <ul className="text-[11px] leading-tight space-y-0.5 pl-1 max-h-48 overflow-y-auto">
        {names.map((name, idx) => (
          <li key={idx} className="break-words">• {name}</li>
        ))}
      </ul>
    </div>
  ) : null;

  return (
    <>
      <button
        ref={badgeRef}
        type="button"
        className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200 transition-colors flex-shrink-0"
        onClick={(e) => { e.preventDefault(); setIsOpen((v) => !v); }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onTouchEnd={(e) => { e.preventDefault(); setIsOpen((v) => !v); }}
        data-testid="still-here-past-reminder-badge"
        aria-label={`${count} children still here past reminder time, click to see names`}
      >
        <Clock className="w-3.5 h-3.5" />
        {count} Still Here Past Reminder Time
      </button>
      {popoverNode && createPortal(popoverNode, document.body)}
    </>
  );
};

export const StillInCenterByCategory = ({ fullTime, schoolAge, partTime, afterSchool, lastUpdated, pastReminderChildNames }) => {
  const updated = lastUpdated || format(new Date(), 'h:mm a');
  return (
    <div
      className="mt-4 rounded-xl p-4"
      style={{
        background: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(59,130,246,0.12)',
        border: '2px solid rgba(59,130,246,0.3)'
      }}
      data-testid="still-in-center-section"
    >
      <div className="flex items-start justify-between mb-1 flex-wrap gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-blue-900 flex items-center gap-2">
            WHO IS STILL IN THE CENTER BY CATEGORY
            <Info className="w-4 h-4 text-blue-400" />
          </h3>
          <p className="text-xs text-gray-600 mt-0.5">
            Children who have not been signed out yet. Hover or tap a card to see names.
          </p>
        </div>
        <span className="text-xs text-gray-500 flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
          <PastReminderBadge count={(pastReminderChildNames || []).length} names={pastReminderChildNames || []} />
          <span className="flex items-center gap-1.5">
            <RefreshCw className="w-3 h-3" />
            Last updated: {updated}
          </span>
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
        <RatioCard
          title="FULL TIME"
          color="#1e40af" bg="rgba(239,246,255,0.9)" border="rgba(59,130,246,0.4)" dot="#3b82f6"
          testid="ratio-card-full-time"
          {...fullTime}
        />
        <RatioCard
          title="SCHOOL AGE"
          color="#15803d" bg="rgba(240,253,244,0.9)" border="rgba(34,197,94,0.4)" dot="#22c55e"
          testid="ratio-card-school-age"
          {...schoolAge}
        />
        <RatioCard
          title="PART TIME"
          color="#7c3aed" bg="rgba(250,245,255,0.9)" border="rgba(147,51,234,0.4)" dot="#a855f7"
          testid="ratio-card-part-time"
          {...partTime}
        />
        <RatioCard
          title="AFTER SCHOOL"
          color="#c2410c" bg="rgba(255,247,237,0.9)" border="rgba(249,115,22,0.4)" dot="#f97316"
          testid="ratio-card-after-school"
          {...afterSchool}
        />
      </div>
    </div>
  );
};

export default StillInCenterByCategory;
